import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import {
  getParentChildDocumentDownloadUrl,
  listParentChildDocuments,
  uploadParentChildDocuments,
  type StudentMyDocumentRow,
} from '../../../lib/studentDossierApi';

function formatDateTimeRo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ro-RO');
}

const docTypeOptions = [
  { value: 'dosar_inscriere', label: 'Dosar înscriere' },
  { value: 'certificat_nastere', label: 'Certificat naștere' },
  { value: 'ci_parinte', label: 'CI părinte' },
  { value: 'adeverinta_medicala', label: 'Adeverință medicală' },
  { value: 'cerere_transfer', label: 'Cerere transfer' },
  { value: 'altele', label: 'Altele' },
];

type Props = {
  childId: number;
  childName?: string | null;
};

export default function ParentSecretariatPanel({ childId, childName }: Props) {
  const [docs, setDocs] = useState<StudentMyDocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [docType, setDocType] = useState<string>('dosar_inscriere');
  const [title, setTitle] = useState<string>('');

  const loadDocs = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    setError('');
    try {
      const rows = await listParentChildDocuments(childId);
      setDocs(rows);
    } catch (e: any) {
      setDocs([]);
      setError(e?.response?.data?.detail || 'Nu am putut încărca documentele.');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    void loadDocs();
  }, [loadDocs]);

  const byTypeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of docTypeOptions) map.set(String(o.value), String(o.label));
    return map;
  }, []);

  const submitUpload = useCallback(
    async (files: File[]) => {
      if (!files.length || !childId) return;
      setUploadBusy(true);
      setUploadError('');
      try {
        await uploadParentChildDocuments(childId, { files, doc_type: docType, title: title.trim() || null });
        setTitle('');
        await loadDocs();
      } catch (e: any) {
        setUploadError(e?.response?.data?.detail || 'Nu am putut încărca fișierele.');
      } finally {
        setUploadBusy(false);
      }
    },
    [childId, docType, loadDocs, title]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Secretariat</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Încarci documente pentru dosarul digital{childName ? `: ${childName}` : ''}.
            </p>
          </div>
          <Button variant="outline" iconName="RefreshCw" onClick={() => void loadDocs()} loading={loading}>
            Reîncarcă
          </Button>
        </div>

        <div className="mt-4 border border-border rounded-lg p-4 bg-background">
          <div className="text-sm font-semibold text-foreground">Încarcă document(e)</div>
          {uploadError ? <div className="mt-2 text-sm text-error">{uploadError}</div> : null}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select label="Tip document" value={docType} options={docTypeOptions} onChange={(v) => setDocType(String(v || 'dosar_inscriere'))} />
            <Input label="Titlu (opțional)" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="file"
              multiple
              disabled={uploadBusy}
              onChange={(e) => {
                const fs = Array.from(e.target.files || []);
                if (!fs.length) return;
                void submitUpload(fs);
                e.currentTarget.value = '';
              }}
            />
            <div className="text-xs text-muted-foreground">PDF/JPG/PNG/DOCX, max 10MB/fișier.</div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-foreground">Documente</div>
            <div className="text-xs text-muted-foreground mt-1">{docs.length}</div>
          </div>
        </div>

        {error ? <div className="mt-3 text-sm text-error">{error}</div> : null}

        <div className="mt-4 space-y-2 max-h-[520px] overflow-auto pr-1">
          {loading && docs.length === 0 ? <div className="text-sm text-muted-foreground">Se încarcă…</div> : null}
          {!loading && docs.length === 0 ? <div className="text-sm text-muted-foreground">Nu există documente încă.</div> : null}
          {docs.map((d) => {
            const typeLabel = (d.doc_type ? byTypeLabel.get(String(d.doc_type)) : null) || d.doc_type || '—';
            const role = (d.uploaded_by_role || '').toLowerCase();
            const who = role === 'secretariat' ? 'Secretariat' : role === 'parinte' ? 'Tu' : d.uploaded_by_name || '—';
            return (
              <div key={d.id} className="border border-border rounded-lg p-3 bg-background flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{d.original_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {typeLabel}
                    {d.title ? ` • ${d.title}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Încărcat: {formatDateTimeRo(d.created_at)} • de: {who}
                  </div>
                </div>
                <button
                  type="button"
                  className="p-2 rounded-md hover:bg-muted transition-colors"
                  title="Descarcă"
                  onClick={() => window.open(getParentChildDocumentDownloadUrl(childId, d.id), '_blank')}
                >
                  <Icon name="Download" size={18} className="text-muted-foreground" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

